import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../Modal';
import ImageContext, { type ImageContextValue } from '../../ImageContext';
import Spinner from '../../Spinner';
import { RSAlert, RSButton } from 'reactsymbols-kit';
import { saveAs } from 'file-saver';
import { exportEDGImage, exportFSMImage, type ExportedImage } from '../../../diagram/exportImage';
import Styles from '../../../styles/colors';
import type { IModel } from '../../../stores/model';
import type { IFSM } from '../../../stores/model/metamodel/fsm';
import type { Notation } from '../../../diagram/ObjectTypeNode';

/** Which diagram the modal exports an image of. */
type ExportWhat = 'edg' | 'oet' | 'fsm';

/**
 * A capture function: it triggers the image capture and resolves to the captured
 * payload (the `{ image }` blob the modal saves). The OET capture (from
 * `ImageContext`) resolves an opaque payload at the context boundary; the
 * factory below narrows it to this shape (it does resolve `{ image }` at runtime).
 */
type CaptureImage = () => Promise<ExportedImage>;

/** Props for the inner {@link ExportImage} content. */
interface ExportImageContentProps {
  /** The capture function, injected by the wrapper from the context/exporters. */
  captureImage?: CaptureImage;
  /** The model name used for the saved file. */
  modelName: string;
  /** Which diagram is being exported. */
  what?: ExportWhat;
  /** The visible FSM (only used for the `fsm` filename suffix). */
  fsm?: IFSM;
  /** Close the modal. */
  onRequestClose: () => void;
}

/** Props for the {@link ExportImageModal} wrapper (the default export). */
interface ExportImageModalProps extends ExportImageContentProps {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** The MERODE model (the EDG/FSM exporters read it). */
  model: IModel;
  /** The notation the EDG is exported in. */
  notation: Notation;
}

/**
 * Export an image.
 *
 * Props:
 * - captureImage: function that resolves to { image }, injected from the
 *   ImageContext / lib/joint exporters by the wrapper below.
 * - modelName, what, fsm: used to name the saved file and pick the error copy.
 * - onRequestClose: close the modal.
 */
function ExportImage({ captureImage, modelName, what, fsm, onRequestClose }: ExportImageModalProps) {
  const [exporting, setExporting] = useState(true);
  const [error, setError] = useState(false);

  // Keep the latest props in refs so doCapture (mount effect + retry button)
  // always sees fresh values without re-registering.
  const propsRef = useRef({ captureImage, modelName, what, fsm, onRequestClose });
  propsRef.current = { captureImage, modelName, what, fsm, onRequestClose };

  /**
   * Save the image
   */
  const saveImage = (image: Blob) => {
    const { modelName, what, fsm } = propsRef.current;
    // `fsm` is only read on the `what === 'fsm'` path, where the visible FSM is
    // always present (the modal opens for a specific FSM); assert non-null to
    // keep byte-identical runtime without adding a guard.
    const nameSuffix = what === 'fsm' ? `_${fsm!.objectType.name}_${fsm!.name}` : '';
    saveAs(image, `${modelName}_${what}${nameSuffix}.png`);
  };

  /**
   * Capture and save an image.
   */
  const doCapture = () => {
    const { captureImage, onRequestClose } = propsRef.current;
    if (captureImage != null) {
      setExporting(true);
      setError(false);
      captureImage()
        .then(({ image }) => {
          saveImage(image);
          onRequestClose();
        })
        .catch((err: unknown) => {
          console.error(err);
          setExporting(false);
          setError(true);
        });
    } else {
      setError(true);
      setExporting(false);
    }
  };

  // Start capturing an image on component load (componentDidMount)
  useEffect(() => {
    doCapture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * What error message should we show if something goes wrong.
   */
  const errorMessage = () => {
    switch (what) {
      case 'edg':
        return `We couldn't capture an image of the EDG because of an unexpected error.`;
      case 'oet':
        return `We couldn't capture an image of the OET. Please note this is only supported in Google Chrome, Mozilla Firefox and Microsoft Edge.`;
      case 'fsm':
        return `We couldn't capture an image of the FSM because of an unexpected error.`;
      default:
        return null;
    }
  };

  // Render either a spinner or an error
  const renderContent = () => {
    if (exporting) {
      return (
        <React.Fragment>
          <Spinner />
          Please be patient while we capture your image.
        </React.Fragment>
      );
    } else if (error) {
      return (
        <React.Fragment>
          <RSAlert level="danger" visible={true}>
            <p>{errorMessage()}</p>
          </RSAlert>
          <div className="button-wrapper">
            <RSButton
              type="submit"
              background={Styles.blueGradient}
              onClick={doCapture}
              value="Retry"
            />
            <RSButton
              type="button"
              background={Styles.defaultGradient}
              color={Styles.darkTextColor}
              onClick={onRequestClose}
              value="Close"
            />
          </div>
        </React.Fragment>
      );
    } else {
      return null;
    }
  };

  return <div className="export-image-modal">{renderContent()}</div>;
}

/**
 * Wrap in a Modal and inject the correct capture function from the
 * ImageContext or from our lib/joint image exporters.
 */
const captureImageFactory = (
  props: ExportImageModalProps,
  context: ImageContextValue
): CaptureImage | undefined => {
  switch (props.what) {
    case 'edg':
      return () => exportEDGImage(props.model, props.notation);
    case 'oet':
      // The OET capture resolves `{ image }` at runtime, but `ImageContext` types
      // its payload opaque (`void | Promise<unknown>`). Re-type it to the
      // `CaptureImage` shape the modal saves; the runtime function is unchanged.
      return context.captureOET as unknown as CaptureImage;
    case 'fsm':
      return () => exportFSMImage(props.model, props.fsm);
    default:
      return undefined;
  }
};

const ExportImageModal = (props: ExportImageModalProps) => {
  return (
    <Modal isOpen={props.isOpen}>
      {
        <ImageContext.Consumer>
          {(value) => <ExportImage captureImage={captureImageFactory(props, value)} {...props} />}
        </ImageContext.Consumer>
      }
    </Modal>
  );
};

export default ExportImageModal;
